import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Loader2, CheckCircle2, ChevronRight, ChevronLeft, User, Flame, Package, Calendar, FileText, ShieldCheck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { PlzCityInput, StreetAutocomplete } from "@/components/PlzCityInput";

const inputCls = "h-11 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[14px] transition-colors";
const labelCls = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";

const HEATING_OPTIONS = [
  { key: "gas",        label: "Gas",         units: ["m3", "kwh"] },
  { key: "oel",        label: "Öl",          units: ["liter"] },
  { key: "pellet",     label: "Pellet",      units: ["kg", "tonnen"] },
  { key: "holz",       label: "Holz",        units: ["raummeter"] },
  { key: "fernwaerme", label: "Fernwärme",   units: ["kwh"] },
  { key: "waermepumpe",label: "Wärmepumpe",  units: ["kwh"] },
  { key: "strom",      label: "Strom",       units: ["kwh"] },
  { key: "sonstige",   label: "Sonstige",    units: ["kwh", "liter", "m3", "kg", "raummeter"] },
];

const PACKAGES = [
  { key: "guenstig", label: "Günstig", color: "#60A5FA", subtitle: "Preis-Einstieg" },
  { key: "komfort",  label: "Komfort", color: "#34D399", subtitle: "Bestes P/L"   },
  { key: "premium",  label: "Premium", color: "#F59E0B", subtitle: "Top-Marken"   },
];

const YEAR_OPTIONS = [
  { key: "<1970",      label: "vor 1970" },
  { key: "1970-1990",  label: "1970 – 1990" },
  { key: "1991-2010",  label: "1991 – 2010" },
  { key: "2011-2020",  label: "2011 – 2020" },
  { key: ">2020",      label: "nach 2020" },
];

const RENOVATION_OPTIONS = [
  { key: "unsaniert",   label: "Unsaniert",    hint: "Originalzustand" },
  { key: "teilsaniert", label: "Teilsaniert",  hint: "1 Maßnahme (Dach/Fenster/Fassade)" },
  { key: "saniert",     label: "Saniert",      hint: "≥ 2 Maßnahmen" },
  { key: "kfw_neubau",  label: "KfW-Neubau",   hint: "KfW-55/40-Standard" },
];

const initialForm = () => ({
  salutation: "Herr",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  zip: "",
  city: "",
  street: "",
  current_heating: "",
  consumption_value: "",
  consumption_unit: "",
  living_area_sqm: "",
  occupants: "",
  construction_year: "",
  renovation_status: "unsaniert",
  heating_age_years: "",
  // Iter88z: KfW-Fördersatz-Angaben — vom Agent erfasst
  zve_stufe: "",              // "" | "bis30k" | "bis40k" | "bis40k_kind" | "bis50k" | "ueber50k"
  alte_heizung_status: "alt_entsorgt",
  hat_minderjaehriges_kind: false,
  // Iter88zc (17.02.2026): Neubau schließt KfW 458 komplett aus.
  is_neubau: false,
  pv_existing: false,
  electricity_kwh_per_year: "",
  // PV-Interesse (Opt-in für PV-Empfehlung im Orientierungsangebot)
  pv_interested: false,
  roof_orientation: "",
  roof_area_sqm: "",
  has_ev: false,
  wants_battery: false,
  package: "",
  appointment_type: "",
  appointment_date: "",
  appointment_time: "",
  agent_note: "",
  consent_privacy: false,
  consent_partner_routing: false,
});

export default function TelesalesCapture({ embedded = false }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const entryId = params.get("entry_id") || null;
  const [form, setForm] = useState(() => {
    const base = initialForm();
    // Prefill from query params (Next-Call flow)
    ["salutation", "first_name", "last_name", "email", "phone", "zip", "city", "street"].forEach((k) => {
      const v = params.get(k);
      if (v) base[k] = v;
    });
    return base;
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // {lead_id, orientation_offer, review_required}
  const [submitError, setSubmitError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const availableUnits = useMemo(() => {
    const h = HEATING_OPTIONS.find((x) => x.key === form.current_heating);
    return h ? h.units : [];
  }, [form.current_heating]);

  useEffect(() => {
    if (form.current_heating && !availableUnits.includes(form.consumption_unit)) {
      set("consumption_unit", availableUnits[0] || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.current_heating]);

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Front-end validation summary (matching backend rules)
  const errors = useMemo(() => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Pflicht";
    if (!form.last_name.trim()) e.last_name = "Pflicht";
    if (!form.email && !form.phone) e.contact = "E-Mail oder Telefon angeben";
    if (form.zip && !/^\d{5}$/.test(form.zip)) e.zip = "5 Ziffern";
    if (!form.current_heating) e.current_heating = "Pflicht";
    if (!form.package) e.package = "Pflicht";
    if (!form.appointment_type) e.appointment_type = "Pflicht";
    if (!form.appointment_date) e.appointment_date = "Pflicht";
    else if (form.appointment_date < todayIso) e.appointment_date = "≥ heute";
    if (!form.appointment_time) e.appointment_time = "Pflicht";
    if (!form.consent_privacy) e.consent_privacy = "DSGVO bestätigen";
    return e;
  }, [form, todayIso]);

  const isValid = Object.keys(errors).length === 0;

  const submit = async () => {
    console.log("[TelesalesCapture] submit clicked, isValid=", isValid, "errors=", errors);
    setSubmitError(null);
    if (!isValid) {
      const firstErr = Object.entries(errors)[0];
      const msg = firstErr ? `Pflichtfeld: ${firstErr[0]} (${firstErr[1]})` : "Bitte alle Pflichtfelder ausfüllen";
      toast.error(msg);
      setSubmitError(msg);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        consumption_value: form.consumption_value ? Number(form.consumption_value) : null,
        living_area_sqm: form.living_area_sqm ? Number(form.living_area_sqm) : null,
        occupants: form.occupants ? Number(form.occupants) : null,
        heating_age_years: form.heating_age_years ? Number(form.heating_age_years) : null,
        electricity_kwh_per_year: form.electricity_kwh_per_year ? Number(form.electricity_kwh_per_year) : null,
        pv_existing: Boolean(form.pv_existing),
        pv_interested: Boolean(form.pv_interested),
        roof_orientation: form.roof_orientation || null,
        roof_area_sqm: form.roof_area_sqm ? Number(form.roof_area_sqm) : null,
        has_ev: Boolean(form.has_ev),
        wants_battery: Boolean(form.wants_battery),
        zip: form.zip || null,
        email: form.email || null,
        phone: form.phone || null,
        entry_id: entryId,
      };
      console.log("[TelesalesCapture] POST /telesales/capture", payload);
      const { data } = await api.post("/telesales/capture", payload);
      console.log("[TelesalesCapture] POST OK", data);
      setSuccess({
        lead_id: data.lead_id,
        orientation_offer: data.orientation_offer,
        cc_email: data.cc_email,
        review_required: data.review_required,
        status: data.status,
      });
      toast.success("Lead erfolgreich erfasst");
    } catch (e) {
      console.error("[TelesalesCapture] POST FAILED", e?.response?.status, e?.response?.data, e);
      const status = e?.response?.status;
      const raw = e?.response?.data?.detail;
      let msg;
      if (Array.isArray(raw)) {
        msg = raw.map((x) => `${x.loc?.slice(-1)[0] || "?"}: ${x.msg}`).join(" · ");
      } else if (typeof raw === "string") {
        msg = raw;
      } else {
        msg = e?.message || "Erfassung fehlgeschlagen";
      }
      const fullMsg = status ? `HTTP ${status} — ${msg}` : msg;
      toast.error(fullMsg, { duration: 12000 });
      setSubmitError(fullMsg);
    } finally { setSubmitting(false); }
  };

  if (success) {
    const wrapperCls = embedded
      ? "px-1 py-2"
      : "min-h-screen bg-[#09090B]";
    return (
      <div className={wrapperCls} data-testid="telesales-success">
        {!embedded && <AppHeader title="Telesales" back="/dashboard" />}
        <main className={embedded ? "" : "max-w-2xl mx-auto px-4 py-12"}>
          <div className="bg-[#18181B] border border-[#00E575]/40 rounded-2xl p-8 text-center">
            <div className="inline-flex h-16 w-16 rounded-full bg-[#00E575]/15 items-center justify-center mb-4">
              <CheckCircle2 className="text-[#00E575]" size={36} />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2">Lead erfasst ✓</h2>
            <p className="text-zinc-400 text-sm mb-6">
              {success.review_required ? (
                <>Der Lead liegt jetzt in der <b className="text-amber-300">Direktor-Freigabe</b>. 
                  Das Orientierungsangebot wird parallel an den Kunden gemailt
                  {success.cc_email && <> (CC: <b className="text-zinc-200">{success.cc_email}</b>)</>}.</>
              ) : (
                <>Das Orientierungsangebot wird gerade per E-Mail an den Kunden versendet
                  {success.cc_email && <> (CC: <b className="text-zinc-200">{success.cc_email}</b>)</>}.</>
              )}
            </p>
            <div className="bg-[#09090B] border border-zinc-800 rounded-lg p-4 text-left mb-6">
              <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">Lead-ID</div>
              <div className="font-mono text-[12px] text-zinc-300 break-all">{success.lead_id}</div>
              {success.orientation_offer?.heat_load_kw && (
                <>
                  <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mt-3 mb-1">Heizlast (geschätzt)</div>
                  <div className="font-mono text-zinc-100">{success.orientation_offer.heat_load_kw} kW</div>
                </>
              )}
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => {
                  if (entryId) {
                    navigate("/telesales/next-call");
                  } else {
                    setForm(initialForm()); setSuccess(null);
                  }
                }}
                data-testid="telesales-new-lead"
                className="h-11 px-5 rounded-xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center gap-2 hover:bg-[#00C964]"
              >
                <Phone size={16} /> Nächster Anruf
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className="h-11 px-5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
              >
                Zum Dashboard
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "min-h-screen bg-[#09090B]"} data-testid="telesales-capture">
      {!embedded && <AppHeader title="Telesales" back="/dashboard" />}
      <main className={embedded ? "space-y-4" : "max-w-3xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4"}>
        {!embedded && (
          <div className="flex items-center gap-3 mb-1">
            <Phone className="text-[#00E575]" size={22} />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">Telesales — Schnellerfassung</h1>
              <p className="text-[12px] text-zinc-300 font-medium">30-Sekunden-Formular während des Telefonats</p>
            </div>
          </div>
        )}

        {/* Block 1 – Kunde */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-6 w-6 rounded-full bg-[#00E575]/15 text-[#00E575] text-[12px] font-bold inline-flex items-center justify-center">1</span>
            <h2 className="font-bold text-[14px]"><User size={13} className="inline mr-1" /> Kunde</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>Anrede</label>
              <select value={form.salutation} onChange={(e) => set("salutation", e.target.value)} data-testid="ts-salutation" className={inputCls}>
                <option value="Herr">Herr</option>
                <option value="Frau">Frau</option>
                <option value="Divers">Divers</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Vorname *</label>
              <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="ts-first-name" className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Nachname *</label>
              <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="ts-last-name" className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>E-Mail {!form.phone && <span className="text-amber-400">(oder Telefon)</span>}</label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="ts-email" className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Telefon {!form.email && <span className="text-amber-400">(oder E-Mail)</span>}</label>
              <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ts-phone" className={inputCls} />
              {errors.contact && <p className="text-[12px] text-red-400 mt-0.5">{errors.contact}</p>}
            </div>
            {/* PLZ + Ort mit Auto-Lookup (openplzapi.org) */}
            <div className="col-span-2 sm:col-span-1">
              <PlzCityInput
                zip={form.zip}
                city={form.city}
                onZipChange={(v) => set("zip", v)}
                onCityChange={(v) => set("city", v)}
                zipTestId="ts-zip"
                cityTestId="ts-city"
              />
              {errors.zip && <p className="text-[12px] text-red-400 mt-0.5">{errors.zip}</p>}
            </div>
            {/* Straße mit Autocomplete (sobald PLZ 5-stellig) */}
            <div className="col-span-2 sm:col-span-4">
              <StreetAutocomplete
                zip={form.zip}
                street={form.street}
                onStreetChange={(v) => set("street", v)}
                testid="ts-street"
              />
            </div>
          </div>
        </section>

        {/* Block 2 – Heizung */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-6 w-6 rounded-full bg-[#00E575]/15 text-[#00E575] text-[12px] font-bold inline-flex items-center justify-center">2</span>
            <h2 className="font-bold text-[14px]"><Flame size={13} className="inline mr-1" /> Heizsituation</h2>
          </div>
          <div className="mb-3">
            <label className={labelCls}>Aktuelle Heizung *</label>
            <div className="flex flex-wrap gap-1.5">
              {HEATING_OPTIONS.map((h) => (
                <button key={h.key} type="button"
                  onClick={() => set("current_heating", h.key)}
                  data-testid={`ts-heating-${h.key}`}
                  className={`px-3 py-1.5 rounded-lg text-[12px] font-bold border transition-colors ${
                    form.current_heating === h.key
                      ? "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40"
                      : "bg-[#09090B] text-zinc-400 border-zinc-700 hover:border-zinc-500"
                  }`}>
                  {h.label}
                </button>
              ))}
            </div>
            {errors.current_heating && <p className="text-[12px] text-red-400 mt-1">{errors.current_heating}</p>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>Verbrauch / Jahr</label>
              <input type="number" value={form.consumption_value} onChange={(e) => set("consumption_value", e.target.value)} data-testid="ts-consumption-value" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Einheit</label>
              <select value={form.consumption_unit} onChange={(e) => set("consumption_unit", e.target.value)} data-testid="ts-consumption-unit" disabled={availableUnits.length === 0} className={inputCls}>
                <option value="">—</option>
                {availableUnits.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Wohnfläche m²</label>
              <input type="number" value={form.living_area_sqm} onChange={(e) => set("living_area_sqm", e.target.value)} data-testid="ts-living-area" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Personen</label>
              <input type="number" value={form.occupants} onChange={(e) => set("occupants", e.target.value)} data-testid="ts-occupants" className={inputCls} />
            </div>
            <div className="col-span-2 sm:col-span-4">
              <label className={labelCls}>Baujahr</label>
              <div className="flex flex-wrap gap-1.5">
                {YEAR_OPTIONS.map((y) => (
                  <button key={y.key} type="button"
                    onClick={() => set("construction_year", y.key)}
                    data-testid={`ts-year-${y.key}`}
                    className={`px-3 py-1.5 rounded-lg text-[12px] border transition-colors ${
                      form.construction_year === y.key
                        ? "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40"
                        : "bg-[#09090B] text-zinc-400 border-zinc-700 hover:border-zinc-500"
                    }`}>
                    {y.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Sanierungszustand — beeinflusst Volllaststunden & damit Heizlast.
                Default `unsaniert` = konservativste Annahme (höchste Heizlast). */}
            <div className="col-span-2 sm:col-span-4">
              <label className={labelCls}>Sanierungszustand</label>
              <div className="flex flex-wrap gap-1.5">
                {RENOVATION_OPTIONS.map((r) => (
                  <button key={r.key} type="button"
                    onClick={() => set("renovation_status", r.key)}
                    data-testid={`ts-reno-${r.key}`}
                    title={r.hint}
                    className={`px-3 py-1.5 rounded-lg text-[12px] border transition-colors ${
                      form.renovation_status === r.key
                        ? "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40"
                        : "bg-[#09090B] text-zinc-400 border-zinc-700 hover:border-zinc-500"
                    }`}>
                    {r.label}
                  </button>
                ))}
              </div>
              <div className="text-[12px] text-zinc-300 mt-1">
                Default = unsaniert. Beeinflusst die Heizlast-Berechnung
                (mehr Sanierung → kleinere WP-Auslegung).
              </div>
            </div>
            {/* Heizungs-Alter — relevant nur bei Gas (Öl bekommt Geschwindigkeitsbonus ohne Altersgrenze) */}
            {(form.current_heating === "gas" || form.current_heating === "Gas") && (
              <div className="col-span-2">
                <label className={labelCls}>Heizungs-Alter (Jahre)</label>
                <input type="number" min="0" max="80"
                  value={form.heating_age_years}
                  onChange={(e) => set("heating_age_years", e.target.value)}
                  data-testid="ts-heating-age"
                  placeholder="z.B. 22"
                  className={inputCls} />
                <div className="text-[12px] text-zinc-300 mt-1">≥ 20 Jahre → +16 % KfW-Geschwindigkeitsbonus (ab 09.07.2026, vorher 20 %)</div>
              </div>
            )}
            {/* Iter88zc: Neubau-Flag — schließt KfW 458 komplett aus */}
            <div className="col-span-2 rounded-xl bg-red-500/8 border border-red-500/30 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="ts-is-neubau"
                  checked={!!form.is_neubau}
                  onChange={(e) => set("is_neubau", e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-red-400 bg-[#0F1418] text-red-500 focus:ring-red-500"
                />
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-red-200">Neubau — keine KfW-458-Förderung</div>
                  <div className="text-[11.5px] text-red-100/80 mt-0.5">
                    Wenn aktiviert, entfällt die gesamte Förderberechnung nach KfW 458 (die Förderung gilt nur für Bestandsgebäude / Heizungstausch). Der Kunde sieht im Orientierungsangebot nur den Bruttopreis.
                  </div>
                </div>
              </label>
            </div>

            {/* Iter88z: Einkommens-Bonus (KfW-Einkommensstufe) — bei Neubau ausgeblendet */}
            {!form.is_neubau && (
            <div className="col-span-2 rounded-xl bg-emerald-500/8 border border-emerald-500/25 p-3 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-[12px] uppercase tracking-wider font-bold text-emerald-200">Einkommens-Bonus (KfW)</span>
                <span className="text-[11px] text-emerald-100/80">Steigert die Förderung bis zu +40 %</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <label className={labelCls}>Zu versteuerndes Haushalts-Einkommen p.a.</label>
                  <select
                    data-testid="ts-zve-stufe"
                    value={form.zve_stufe}
                    onChange={(e) => set("zve_stufe", e.target.value)}
                    className={inputCls}
                  >
                    <option value="">— keine Angabe (0 %) —</option>
                    <option value="bis30k">bis 30.000 € (+40 %)</option>
                    <option value="bis40k">bis 40.000 € (+30 %)</option>
                    <option value="bis40k_kind">bis 40.000 € + minderjähriges Kind (+40 %)</option>
                    <option value="bis50k">bis 50.000 € (+10 %)</option>
                    <option value="ueber50k">über 50.000 € (0 %)</option>
                  </select>
                  <div className="text-[12px] text-zinc-300 mt-1">
                    Kunde muss den Nachweis (letzter Steuerbescheid) beim Antrag hochladen. Bei Unsicherheit „keine Angabe&#34; wählen — der Bonus taucht dann NICHT im Orientierungsangebot auf.
                  </div>
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Alte Heizung — Zustand</label>
                  <select
                    data-testid="ts-alte-heizung-status"
                    value={form.alte_heizung_status || "alt_entsorgt"}
                    onChange={(e) => set("alte_heizung_status", e.target.value)}
                    className={inputCls}
                  >
                    <option value="alt_entsorgt">Wird durch WP ersetzt (Standard) — Klima-Bonus +16 % bei Öl/Kohle/Nachtspeicher bzw. Gas/Biomasse ab 20 J.</option>
                    <option value="funktionstuechtig_bleibt">Funktionstüchtig, bleibt in Betrieb — kein Klima-Bonus</option>
                    <option value="defekt">Defekt, muss sowieso raus — kein Klima-Bonus (nur funktionsfähige Anlagen)</option>
                  </select>
                </div>
              </div>
            </div>
            )}
            {/* PV vorhanden */}
            <div className="col-span-2 sm:col-span-1">
              <label className={labelCls}>PV-Anlage vorhanden?</label>
              <label className="flex items-center gap-2 cursor-pointer h-10 px-3 bg-[#09090B] border border-zinc-700 rounded-lg">
                <input type="checkbox" checked={form.pv_existing}
                  onChange={(e) => set("pv_existing", e.target.checked)}
                  data-testid="ts-pv-existing"
                  className="accent-amber-400" />
                <span className="text-[12px] text-zinc-300 font-medium">Ja (−15 % WP-Strom)</span>
              </label>
            </div>
            {/* Stromverbrauch p.a. */}
            <div className="col-span-2 sm:col-span-1">
              <label className={labelCls}>Stromverbrauch p.a. (kWh)</label>
              <input type="number" min="0"
                value={form.electricity_kwh_per_year}
                onChange={(e) => set("electricity_kwh_per_year", e.target.value)}
                data-testid="ts-electricity-kwh"
                placeholder="Default: 3.000"
                className={inputCls} />
            </div>
          </div>
        </section>

        {/* ── BLOCK PV-Interesse ─────────────────────────────────────── */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-base font-bold text-amber-400">
              Photovoltaik (optional)
            </h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.pv_interested}
                onChange={(e) => set("pv_interested", e.target.checked)}
                data-testid="ts-pv-interested"
                className="accent-amber-400 h-4 w-4"
              />
              <span className="text-[12px] text-zinc-300 font-semibold">
                Kunde interessiert an PV-Anlage
              </span>
            </label>
          </div>
          {form.pv_interested ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <label className={labelCls}>Dachausrichtung</label>
                <select
                  value={form.roof_orientation}
                  onChange={(e) => set("roof_orientation", e.target.value)}
                  data-testid="ts-roof-orientation"
                  className={inputCls}
                >
                  <option value="">— bitte wählen —</option>
                  <option value="sued">Süd</option>
                  <option value="sued_ost">Süd-Ost</option>
                  <option value="sued_west">Süd-West</option>
                  <option value="ost_west">Ost-West (Doppel)</option>
                  <option value="ost">Ost</option>
                  <option value="west">West</option>
                  <option value="nord">Nord</option>
                </select>
                <div className="text-[12px] text-zinc-300 mt-1">
                  Süd-Ausrichtung bringt ~980 kWh/kWp, Ost-West ~870, Nord ~650.
                </div>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className={labelCls}>Dachfläche (m², optional)</label>
                <input
                  type="number"
                  min="0"
                  value={form.roof_area_sqm}
                  onChange={(e) => set("roof_area_sqm", e.target.value)}
                  data-testid="ts-roof-area"
                  placeholder="z.B. 60"
                  className={inputCls}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className={labelCls}>E-Auto vorhanden oder geplant?</label>
                <label className="flex items-center gap-2 cursor-pointer h-10 px-3 bg-[#09090B] border border-zinc-700 rounded-lg">
                  <input
                    type="checkbox"
                    checked={form.has_ev}
                    onChange={(e) => set("has_ev", e.target.checked)}
                    data-testid="ts-has-ev"
                    className="accent-amber-400"
                  />
                  <span className="text-[12px] text-zinc-300 font-medium">
                    Ja (+3 kWp Anlagengröße empfohlen)
                  </span>
                </label>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className={labelCls}>Stromspeicher gewünscht?</label>
                <label className="flex items-center gap-2 cursor-pointer h-10 px-3 bg-[#09090B] border border-zinc-700 rounded-lg">
                  <input
                    type="checkbox"
                    checked={form.wants_battery}
                    onChange={(e) => set("wants_battery", e.target.checked)}
                    data-testid="ts-wants-battery"
                    className="accent-amber-400"
                  />
                  <span className="text-[12px] text-zinc-300 font-medium">
                    Ja (Eigenverbrauchsquote 30 → 65 %)
                  </span>
                </label>
              </div>
              <div className="col-span-2 text-[12px] text-amber-300/70 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2 mt-1">
                Im Orientierungsangebot erscheint zusätzlich ein PV-Block
                mit empfohlener kWp-Größe, Jahresertrag und voraussichtlicher
                Einsparung. Auto-Pick aus dem Mandanten-PV-Paketkatalog.
              </div>
            </div>
          ) : (
            <div className="text-[12px] text-zinc-300 font-medium">
              Nur ankreuzen, wenn der Kunde explizit nach einer PV-Anlage gefragt
              hat oder Interesse signalisiert hat. Sonst nicht aktivieren.
            </div>
          )}
        </section>

        {/* Block 3 – Paket */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-6 w-6 rounded-full bg-[#00E575]/15 text-[#00E575] text-[12px] font-bold inline-flex items-center justify-center">3</span>
            <h2 className="font-bold text-[14px]"><Package size={13} className="inline mr-1" /> Paket-Wunsch *</h2>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {PACKAGES.map((p) => (
              <button key={p.key} type="button"
                onClick={() => set("package", p.key)}
                data-testid={`ts-package-${p.key}`}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  form.package === p.key
                    ? "border-[#00E575] bg-[#00E575]/5"
                    : "border-zinc-800 bg-[#09090B] hover:border-zinc-600"
                }`}
                style={form.package === p.key ? { boxShadow: `0 0 0 3px ${p.color}30` } : {}}>
                <div className="font-bold text-[14px]" style={{ color: p.color }}>{p.label}</div>
                <div className="text-[12px] text-zinc-400 mt-0.5">{p.subtitle}</div>
              </button>
            ))}
          </div>
          {errors.package && <p className="text-[12px] text-red-400 mt-2">{errors.package}</p>}
        </section>

        {/* Block 4 – Termin */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-6 w-6 rounded-full bg-[#00E575]/15 text-[#00E575] text-[12px] font-bold inline-flex items-center justify-center">4</span>
            <h2 className="font-bold text-[14px]"><Calendar size={13} className="inline mr-1" /> Terminvereinbarung</h2>
          </div>
          <div className="mb-3">
            <label className={labelCls}>Terminart *</label>
            <div className="flex gap-2">
              {[{ k: "vor_ort", l: "Vor-Ort" }, { k: "telefon", l: "Telefon" }].map((x) => (
                <button key={x.k} type="button"
                  onClick={() => set("appointment_type", x.k)}
                  data-testid={`ts-appt-${x.k}`}
                  className={`flex-1 h-10 rounded-lg text-[13px] font-bold border transition-colors ${
                    form.appointment_type === x.k
                      ? "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40"
                      : "bg-[#09090B] text-zinc-400 border-zinc-700 hover:border-zinc-500"
                  }`}>
                  {x.l}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Datum *</label>
              <input type="date" min={todayIso} value={form.appointment_date} onChange={(e) => set("appointment_date", e.target.value)} data-testid="ts-appt-date" className={inputCls} />
              {errors.appointment_date && <p className="text-[12px] text-red-400 mt-0.5">{errors.appointment_date}</p>}
            </div>
            <div>
              <label className={labelCls}>Uhrzeit *</label>
              <input type="time" step="1800" value={form.appointment_time} onChange={(e) => set("appointment_time", e.target.value)} data-testid="ts-appt-time" className={inputCls} />
            </div>
          </div>
        </section>

        {/* Block 5 – Senden */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-6 w-6 rounded-full bg-[#00E575]/15 text-[#00E575] text-[12px] font-bold inline-flex items-center justify-center">5</span>
            <h2 className="font-bold text-[14px]"><FileText size={13} className="inline mr-1" /> Senden</h2>
          </div>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Notiz (intern)</label>
              <input value={form.agent_note} onChange={(e) => set("agent_note", e.target.value)} data-testid="ts-note" placeholder="z.B. Rückruf nachmittags gewünscht" className={inputCls} />
            </div>
            <label className="flex items-start gap-2 cursor-pointer p-3 bg-[#09090B] border border-zinc-700 rounded-lg">
              <input type="checkbox" checked={form.consent_privacy} onChange={(e) => set("consent_privacy", e.target.checked)} data-testid="ts-consent" className="mt-1 accent-[#00E575]" />
              <span className="text-[12px] text-zinc-300 font-medium">
                <ShieldCheck className="inline mr-1 text-[#00E575]" size={13} />
                Kunde wurde am Telefon über die Datenverarbeitung informiert und hat zugestimmt (DSGVO).
              </span>
            </label>
            {errors.consent_privacy && <p className="text-[12px] text-red-400">{errors.consent_privacy}</p>}
            <label className="flex items-start gap-2 cursor-pointer p-3 bg-[#09090B] border border-zinc-700 rounded-lg">
              <input type="checkbox" checked={form.consent_partner_routing} onChange={(e) => set("consent_partner_routing", e.target.checked)} data-testid="ts-consent-partner" className="mt-1 accent-amber-400" />
              <span className="text-[12px] text-zinc-300 font-medium">
                <ShieldCheck className="inline mr-1 text-amber-400" size={13} />
                Kunde willigt zusätzlich in die Weitergabe seiner Daten an einen unserer Installations-Partner zur Erstellung eines konkreten Angebots ein. <i className="text-zinc-500">Wenn nicht erteilt: Lead bleibt intern beim Mandanten.</i>
              </span>
            </label>
            {submitError && (
              <div
                data-testid="ts-submit-error"
                className="rounded-xl border border-red-500/50 bg-red-500/10 px-3.5 py-3 text-[12.5px] text-red-200"
              >
                <div className="font-bold mb-0.5 inline-flex items-center gap-1.5">
                  <AlertTriangle size={13} /> Erfassung fehlgeschlagen
                </div>
                <div className="text-[12px] text-red-100/90 leading-snug whitespace-pre-wrap font-medium">{submitError}</div>
                <button
                  onClick={() => setSubmitError(null)}
                  className="mt-1.5 text-[10.5px] text-red-200/70 underline hover:text-red-100"
                >
                  Meldung ausblenden
                </button>
              </div>
            )}
            <button
              onClick={submit}
              disabled={!isValid || submitting}
              data-testid="ts-submit"
              className="w-full h-12 rounded-xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#00C964] disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Phone size={16} />}
              Lead senden & Angebot anstoßen
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
